import styled, { css } from 'styled-components';
import React from 'react';
import { Flex, FlexProps } from '@ui-library/flex';
import { Subtext } from '@ui-library/typography';
import {
  createPaddingVariant,
  createBorderRadiusVariant,
  PaddingVariant,
  BorderRadiusVariant,
} from '@ui-library/tokens/utils';

interface CardProp {
  ghost?: boolean;
  notPadded?: boolean;
  fitContent?: boolean;
  borderColor?: string;
  highlighted?: boolean;
  padding?: PaddingVariant;
  borderRadius?: BorderRadiusVariant;
}

const baseCard = css<CardProp>`
  overflow: hidden;
  background-color: ${({ theme }) => theme.token.colorBgBase};
  border: 1px solid ${({ borderColor, theme }) => borderColor || theme.token.colorBorder};

  ${createBorderRadiusVariant('borderRadiusLG')}

  ${({ notPadded }) => !notPadded && createPaddingVariant('paddingLG')}

  ${({ fitContent }) => fitContent && 'width: fit-content;'}

  ${({ highlighted, theme }) =>
    highlighted &&
    `
    background-color: ${theme.token.colorFillAlter};
    border: 1px solid ${theme.token.colorBorderSecondary};
  `}

  ${({ ghost = false }) => ghost && 'border: none;'}
`;

export const Card = styled.div<CardProp>`
  ${baseCard}
`;

type FlexCardProps = CardProp &
  FlexProps & {
    stretch?: boolean;
  };

export const FlexCard = styled(Flex)<FlexCardProps>`
  ${baseCard}
  ${({ stretch }) => stretch && 'flex: 1;'}
`;

export const CardContent = styled.div`
  padding: ${({ theme }) => theme.token.paddingLG}px ${({ theme }) => theme.token.paddingXL}px;
`;

const Header = styled(Flex)`
  background: ${({ theme }) => theme.token.controlItemBgHover};
  border-radius: ${({ theme }) => theme.token.borderRadiusLG}px
    ${({ theme }) => theme.token.borderRadiusLG}px 0 0;
  padding: ${({ theme }) => theme.token.padding}px ${({ theme }) => theme.token.paddingXL}px;
`;

interface IProps {
  children: React.ReactNode;
  title: string;
  tag?: string;
  /** Extra content rendered next to the title (e.g. a filter badge). */
  extra?: React.ReactNode;
  noContentPadding?: boolean;
}

export const HeaderCard = ({ children, title, tag, extra, noContentPadding }: IProps) => (
  <Card notPadded data-testid="card">
    <Header spaceBetween alignItemsCenter>
      <Flex alignItemsCenter gap="0.75rem">
        <Subtext strong data-testid="title">
          {title}
        </Subtext>
        {extra}
      </Flex>
      {tag && <Subtext data-testid="tag">{tag}</Subtext>}
    </Header>
    {noContentPadding ? children : <CardContent>{children}</CardContent>}
  </Card>
);
